import locale from "localization";
import grid from "styles/grid.module.scss";
import ProductCode from "enums/product-code";
import { Text, Checkbox } from "components/commons";
import styles from "./shift-details-stock-inventory.module.scss";
import DipstickDetails from "./shift-details-stock-inventory-dipstick-details.module";
import DipstickInput from "../shift-details-stock-inventory-dipstick-input";
import React, { useCallback, useEffect, useMemo } from "react";
import classNames from "classnames";
import { EmployeePosition, FormMode } from "enums";
import Validation from "utils/validation.utils";
import { validateField } from "hooks/useForm";
import { formatNumber, formatVolume } from "utils";
import { useAuth } from "hooks";
import { validateTotalizers } from "utils/form-data/objects.utils";
import { fieldAnchorId, isFormFieldIncomplete } from "../shift-records-details-completion";

const PRODUCT_CONFIG = {
  [ProductCode.DIESEL]: { ugt: "1", label: "Diesel" },
  [ProductCode.GAS91]: { ugt: "2", label: "Gas 91" },
  [ProductCode.GAS95]: { ugt: "3", label: "Gas 95" },
  [ProductCode.GAS97]: { ugt: "4", label: "Gas 97" },
};

const formatFieldName = (type, str, unit) => {
  const capitalizedStr = str.charAt(0).toUpperCase() + str.slice(1);
  return `${type}${capitalizedStr}${unit}`;
};

export const validateReadingDipstick = (
  name,
  value,
  fields,
  fromCallback,
  shift,
  beginningDisabled,
) => {
  let data = { [name]: { value } };

  const type = name === "isReadingDipstickBeginning" ? "beginning" : "ending";
  const isDidNotPerform = value;

  const isReopen = !!shift?.shift?.closedAt;

  Object.keys(fields).forEach((key) => {
    if (key.includes(type)) {
      const isCm = key.includes("Cm");
      const fieldValue =
        fromCallback && isDidNotPerform && shift && shift[key] != null
          ? formatNumber(shift[key], isCm ? 4 : 3)
          : "";
      const maxLiter = fields[key]?.maxLiter || (isCm ? 99999.9999 : 50000);
      const validations =
        isDidNotPerform || !fields[key].required
          ? []
          : [
              Validation.required({ customMessage: locale.thisFieldIsRequired }),
              Validation.min(isCm ? 0 : 0.001, {
                isFormatted: true,
                customMessage: <locale.Populate items={["0.00"]} text={locale.valueShouldNotBe} />,
              }),
              Validation.max(maxLiter, {
                isFormatted: true,
                customMessage: (
                  <locale.Populate
                    items={[formatNumber(maxLiter, isCm ? 4 : 3)]}
                    text={locale.maxLiterShouldBe}
                  />
                ),
              }),
            ];

      data[key] = validateField(
        {
          ...fields[key],
          disabled: isDidNotPerform,
          value: isReopen ? formatNumber(shift?.shift?.[key], isCm ? 4 : 3) : fieldValue,
          validations,
        },
        fields,
      );
    }
  });

  return data;
};

const ShiftDetailsDipstick = ({
  shift,
  progress,
  pageMode,
  form,
  endDidNotPerformDipStick,
  setEndDidNotPerformDipStick,
}) => {
  const { fields, modifyField, modifyForm } = form;
  const stationProducts = shift.station.stationUgt.map((item) => item.productCode);
  const beginningDisabled = shift.recentShift !== null;

  const hasInitializedRef = React.useRef(false);

  const auth = useAuth();

  const isTotalizerSubmittable = useMemo(() => {
    const isShiftPumpTotalsFieldsIsNotEmpty =
      [EmployeePosition.STATION_HEAD, "portal-admin"].includes(auth.accessRole.role) &&
      form.fields.shiftPumpTotals.value.length > 0
        ? validateTotalizers(form.fields.shiftPumpTotals.value)
        : true;

    return isShiftPumpTotalsFieldsIsNotEmpty;
  }, [auth.accessRole.role, form.fields.shiftPumpTotals.value]);

  const hasAnyEndingTotalizer = useMemo(() => {
    const pumps = form.fields.shiftPumpTotals.value || [];
    if (pumps.length === 0) return false;
    return pumps.some((pump) =>
      Object.keys(pump).some(
        (key) =>
          key.startsWith("endTotalizer") &&
          !key.toLowerCase().includes("old") &&
          pump[key] !== "" &&
          pump[key] != null,
      ),
    );
  }, [form.fields.shiftPumpTotals.value]);

  useEffect(() => {
    form.modifyForm((fields) => {
      const fieldsTemp = {
        ...fields,
      };

      const isEnabled = fieldsTemp["isReadingDipstickEnding"].value === true;

      Object.entries(fieldsTemp).forEach(([key, value]) => {
        if (!key.toLowerCase().includes("ending")) {
          return;
        }

        if (key === "isReadingDipstickEnding") {
          value.disabled = !hasAnyEndingTotalizer || !isTotalizerSubmittable;
        } else {
          value.disabled = isEnabled ? true : isTotalizerSubmittable ? false : true;
        }
      });
      return fieldsTemp;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTotalizerSubmittable, hasAnyEndingTotalizer]);

  useEffect(() => {
    if (hasInitializedRef.current) return;

    const isReopen = !!shift?.shift?.closedAt;

    if (!beginningDisabled && !isReopen) return;

    const data = {};
    stationProducts.forEach((product) => {
      const beginningCm = formatFieldName("beginning", product, "Cm");
      const beginningL = formatFieldName("beginning", product, "");
      const endingCm = formatFieldName("ending", product, "Cm");
      const endingL = formatFieldName("ending", product, "");

      if (beginningDisabled) {
        const wasProductNewlyAdded =
          shift.recentShift.isReadingDipstickEnding !== false &&
          shift.recentShift[endingCm] === 0 &&
          shift.recentShift[endingL] === 0;

        if (!wasProductNewlyAdded) {
          data[beginningCm] = {
            ...fields[beginningCm],
            value: formatNumber(shift.recentShift[endingCm], 4),
          };
          data[beginningL] = {
            ...fields[beginningL],
            value: formatVolume(shift.recentShift[endingL], ""),
          };

          if (isReopen) {
            data[endingCm] = {
              ...fields[endingCm],
              value: formatNumber(shift.shift?.[endingCm], 4),
            };
            data[endingL] = {
              ...fields[endingL],
              value: formatVolume(shift.shift?.[endingL], ""),
            };
          }
        }
      } else if (isReopen) {
        data[beginningCm] = {
          ...fields[beginningCm],
          value: formatNumber(shift.shift?.[beginningCm], 4),
        };
        data[beginningL] = {
          ...fields[beginningL],
          value: formatVolume(shift.shift?.[beginningL], ""),
        };
        data[endingCm] = {
          ...fields[endingCm],
          value: formatNumber(shift.shift?.[endingCm], 4),
        };
        data[endingL] = {
          ...fields[endingL],
          value: formatVolume(shift.shift?.[endingL], ""),
        };
      }
    });

    modifyForm({ ...fields, ...data });
    hasInitializedRef.current = true;
  }, [beginningDisabled, shift.recentShift, stationProducts, modifyForm, fields, shift.shift]);

  const isDidNotPerform = useCallback(
    (name, value) => {
      const validatedFields = validateReadingDipstick(
        name,
        value,
        fields,
        true,
        shift.recentShift,
        beginningDisabled,
      );
      const data = { [name]: { value }, ...validatedFields };

      setEndDidNotPerformDipStick(value);

      modifyForm({ ...fields, ...data });
      return data;
    },
    [beginningDisabled, fields, modifyForm, setEndDidNotPerformDipStick, shift.recentShift],
  );

  const renderDipstickDetails = (product, isEnding = false, position) => (
    <DipstickDetails
      key={product}
      label={`${locale.ugt}${position}`}
      progress={progress}
      shift={shift}
      product={product}
      isEndingDipstick={isEnding}
    />
  );

  const renderDipstickInputs = (product, type = "beginning", position) => {
    const cmField = formatFieldName(type, product, "Cm");
    const literField = formatFieldName(type, product, "");

    const isCmDisabled = fields?.[cmField].disabled;
    const isLiterDisabled = fields?.[literField].disabled;

    return (
      <div className={styles.dipstick} key={`${type}-${product}`}>
        <Text className={classNames(styles.classLabel, styles[product])}>
          {`UGT ${position} (${PRODUCT_CONFIG[product].label})`}
        </Text>
        <div
          className={classNames({
            [`${styles.spacerBottom}`]: type === "beginning",
          })}
        >
          <div className={styles.row}>
            <div
              className={styles.columnFluid}
              id={fieldAnchorId(cmField)}
              data-incomplete={!isCmDisabled && isFormFieldIncomplete(fields?.[cmField])}
            >
              <DipstickInput
                fields={fields?.[cmField]}
                unit="mm"
                placeholder="e.g 1000"
                modifyField={modifyField}
                disabled={isCmDisabled}
              />
            </div>
            <div
              className={styles.columnFluid}
              id={fieldAnchorId(literField)}
              data-incomplete={!isLiterDisabled && isFormFieldIncomplete(fields?.[literField])}
            >
              <DipstickInput
                fields={fields?.[literField]}
                unit="L"
                placeholder="e.g 100"
                decimalPlaces={3}
                modifyField={modifyField}
                disabled={isLiterDisabled}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.subContainer}>
      <div className={grid.row}>
        <div className={styles.column}>
          {pageMode === FormMode.View && (
            <>
              <Text className={styles.header}>{locale.beginningInventory}</Text>
              {!shift?.shift.isReadingDipstickBeginning && progress === 100.0 ? (
                <>
                  <div className={styles.spacer10} />
                  <Text className={styles.content}>{locale.didNotPerformDipstickReading}</Text>
                </>
              ) : (
                stationProducts.map(
                  (product, i) =>
                    PRODUCT_CONFIG[product] && renderDipstickDetails(product, false, i + 1),
                )
              )}
              <div className={styles.spacer50} />
            </>
          )}
        </div>
        <div className={styles.column}>
          {pageMode === FormMode.View && (
            <>
              <Text className={styles.header}>{locale.endingInventory}</Text>
              {!shift?.shift.isReadingDipstickEnding && progress === 100.0 ? (
                <>
                  <div className={styles.spacer10} />
                  <Text className={styles.content}>{locale.didNotPerformDipstickReading}</Text>
                </>
              ) : (
                stationProducts.map(
                  (product, i) =>
                    PRODUCT_CONFIG[product] && renderDipstickDetails(product, true, i + 1),
                )
              )}
              <div className={styles.spacer50} />
            </>
          )}
        </div>
      </div>

      {pageMode === FormMode.Edit && (
        <div className={styles.editSalesContainer}>
          <div className={styles.beginningInventory}>
            <Text className={styles.header}>{locale.beginningInventory}</Text>
            <div className={styles.spacerTop}>
              {stationProducts.map((product, i) =>
                renderDipstickInputs(product, "beginning", i + 1),
              )}
            </div>
          </div>

          <div className={styles.endingInventory}>
            <Text className={styles.header}>{locale.endingInventory}</Text>
            <div className={styles.checkBox}>
              <Checkbox
                {...fields?.isReadingDipstickEnding}
                onChange={(name, { value }) => isDidNotPerform(name, value)}
              />
              {"Did not perform dipstick reading"}
            </div>
            {stationProducts.map((product, i) => renderDipstickInputs(product, "ending", i + 1))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftDetailsDipstick;
